import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiActivity, FiBell, FiGitBranch, FiZap, FiCpu, FiFileText, FiBarChart2, FiMail, FiLock, FiCheck, FiUsers, FiShield, FiEye } from 'react-icons/fi';
import '../../Styles/Sentiment.css';
import { login } from '../../utils/sentimentApi';
import SentimentDashboard from './SentimentDashboard';
import SentimentAlerts from './SentimentAlerts';
import SentimentWorkflow from './SentimentWorkflow';
import SentimentAutomations from './SentimentAutomations';
import SentimentCopilot from './SentimentCopilot';
import SentimentAudit from './SentimentAudit';
import SentimentAnalytics from './SentimentAnalytics';
import SentimentReports from './SentimentReports';
import SentimentUsers from './SentimentUsers';
import SentimentRBAC from './SentimentRBAC';
import SentimentMonitoring from './SentimentMonitoring';

const ALL_TABS = [
  { id: 'dashboard',   label: 'Dashboard',   icon: <FiActivity size={14} />,  roles: null },
  { id: 'alerts',      label: 'Alerts',       icon: <FiBell size={14} />,      roles: null },
  { id: 'workflow',    label: 'Workflow',      icon: <FiGitBranch size={14} />, roles: null },
  { id: 'analytics',   label: 'Analytics',    icon: <FiBarChart2 size={14} />, roles: null },
  { id: 'automations', label: 'Automations',  icon: <FiZap size={14} />,       roles: null },
  { id: 'copilot',     label: 'AI Co-pilot',  icon: <FiCpu size={14} />,       roles: null },
  { id: 'reports',     label: 'Reports',      icon: <FiFileText size={14} />,  roles: null },
  { id: 'audit',       label: 'Audit Log',    icon: <FiFileText size={14} />,  roles: null },
  { id: 'monitoring',  label: 'Monitoring',   icon: <FiEye size={14} />,       roles: ['admin', 'team_lead'] },
  { id: 'users',       label: 'Users',        icon: <FiUsers size={14} />,     roles: ['admin'] },
  { id: 'rbac',        label: 'RBAC',         icon: <FiShield size={14} />,    roles: ['admin'] },
];

const ROLES = [
  { id: 'admin',         name: 'Admin',         desc: 'Full system access' },
  { id: 'team_lead',     name: 'Team Lead',      desc: 'Team view + escalations' },
  { id: 'support_agent', name: 'Support Agent',  desc: 'Own cases + queue' },
  { id: 'sales',         name: 'Sales',          desc: 'Risk view only' },
  { id: 'marketing',     name: 'Marketing',      desc: 'Trends & NPS only' },
  { id: 'finance',       name: 'Finance',        desc: 'Churn risk reports' },
];

const METRICS = [
  { icon: '⭐', label: 'CSAT Score',     val: '4.2',  delta: '↑ 0.3 this week' },
  { icon: '⚡', label: 'Churn Risk',     val: '6.2%', delta: '↓ 1.2% vs last month' },
  { icon: '🔴', label: 'Open P1 Alerts', val: '7',    delta: '3 new today' },
];

const SentimentApp = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isAuthed, setIsAuthed] = useState(!!localStorage.getItem('sentiment_token'));
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('admin');

  const [selectedAlertId, setSelectedAlertId] = useState(null);
  const [toasts, setToasts] = useState([]);

  const currentRole = localStorage.getItem('sentiment_role') || 'admin';

  const visibleTabs = ALL_TABS.filter(t => !t.roles || t.roles.includes(currentRole));

  const showToast = useCallback((msg, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const data = await login(loginEmail, loginPassword);
      localStorage.setItem('sentiment_token', data.access_token);
      localStorage.setItem('sentiment_role', data.user?.role || selectedRole);
      localStorage.setItem('sentiment_user_email', data.user?.email || loginEmail);
      setIsAuthed(true);
    } catch (err) {
      setLoginError(err.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const openWorkflow = (alertId) => {
    setSelectedAlertId(alertId);
    setActiveTab('workflow');
  };

  const handleDisconnect = () => {
    localStorage.removeItem('sentiment_token');
    localStorage.removeItem('sentiment_role');
    localStorage.removeItem('sentiment_user_email');
    setIsAuthed(false);
  };

  if (!isAuthed) {
    return (
      <div className="sm-login-page">
        {/* ── Left branding panel ── */}
        <div className="sm-login-left">
          <div className="sm-login-dotgrid" />
          <div className="sm-login-orb sm-login-orb-1" />
          <div className="sm-login-orb sm-login-orb-2" />
          <div className="sm-login-orb sm-login-orb-3" />

          <div className="sm-login-brand">
            <div className="sm-login-logo">
              <div className="sm-login-logo-icon">📊</div>
              <span className="sm-login-logo-text">SAVI AI</span>
              <span className="sm-login-logo-badge">Sentiment</span>
            </div>
            <h1 className="sm-login-tagline">
              Monitor.<br />
              <span>Understand.</span><br />
              Act.
            </h1>
            <p className="sm-login-sub">
              Real-time customer sentiment intelligence — detect churn signals, auto-escalate P1 alerts, and close the loop faster.
            </p>
          </div>

          <div className="sm-login-metrics">
            {METRICS.map(m => (
              <div className="sm-login-metric-card" key={m.label}>
                <div className="sm-login-metric-icon">{m.icon}</div>
                <div className="sm-login-metric-info">
                  <div className="sm-login-metric-label">{m.label}</div>
                  <div className="sm-login-metric-val">{m.val}</div>
                  <div className="sm-login-metric-delta">{m.delta}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right form panel ── */}
        <div className="sm-login-right">
          <form className="sm-login-form-wrap" onSubmit={handleLogin}>

            <button type="button" className="sm-login-back" onClick={() => navigate('/home')}>
              <FiArrowLeft size={14} /> Back to Home
            </button>

            <div className="sm-login-form-header">
              <h2>Sign in to SAVI</h2>
              <p>Sentiment Monitoring &amp; Actioning Platform</p>
            </div>

            <div className="sm-divider" />

            {/* Role selector */}
            <div className="sm-role-section">
              <span className="sm-role-label">Sign in as</span>
              <div className="sm-role-grid">
                {ROLES.map(r => (
                  <button
                    key={r.id}
                    type="button"
                    className={`sm-role-card ${selectedRole === r.id ? 'selected' : ''}`}
                    onClick={() => setSelectedRole(r.id)}
                  >
                    <span className="sm-role-check"><FiCheck size={9} /></span>
                    <span className="sm-role-name">{r.name}</span>
                    <span className="sm-role-desc">{r.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="sm-divider" />

            <div className="sm-fields-section">
              <div className="sm-field">
                <label>Email</label>
                <div className="sm-input-wrap">
                  <input
                    type="email"
                    placeholder="your.email@company.com"
                    value={loginEmail}
                    onChange={e => setLoginEmail(e.target.value)}
                    required
                    autoFocus
                  />
                  <FiMail className="sm-input-icon" />
                </div>
              </div>
              <div className="sm-field">
                <label>Password</label>
                <div className="sm-input-wrap">
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={e => setLoginPassword(e.target.value)}
                    required
                  />
                  <FiLock className="sm-input-icon" />
                </div>
              </div>
            </div>

            {loginError && (
              <p className="sm-err" key={loginError}>
                <span>⚠</span> {loginError}
              </p>
            )}

            <button className="sm-login-btn" type="submit" disabled={loginLoading}>
              {loginLoading
                ? <span className="sm-btn-dots"><span/><span/><span/></span>
                : 'Sign in to SAVI'
              }
            </button>

            <p className="sm-login-footer">
              Secured by SAVI AI · Enterprise Sentiment Platform
            </p>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="sm-app">
      {/* toast notifications */}
      <div className="sm-toast-wrap" aria-live="polite">
        {toasts.map(t => (
          <div key={t.id} className={`sm-toast sm-toast-${t.type}`}>
            <span className="sm-toast-icon">
              {t.type === 'success' ? '✓' : t.type === 'error' ? '✗' : 'ℹ'}
            </span>
            {t.msg}
          </div>
        ))}
      </div>

      {/* floating ambient orbs */}
      <div className="sm-bg-orbs" aria-hidden="true">
        <span className="sm-bg-orb sm-bg-orb-1" />
        <span className="sm-bg-orb sm-bg-orb-2" />
        <span className="sm-bg-orb sm-bg-orb-3" />
      </div>

      <div className="sm-topbar">
        <button className="sm-topbar-back" onClick={() => navigate('/home')}>
          <FiArrowLeft size={16} /> Back
        </button>
        <span className="sm-topbar-title">Sentiment Monitoring</span>
        <span className="sm-live-badge">
          <span className="sm-live-dot" />
          Live
        </span>
        <button
          className="sm-btn sm-btn-ghost"
          style={{ fontSize: 12 }}
          onClick={handleDisconnect}
        >
          Disconnect
        </button>
      </div>

      <div className="sm-tabs">
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            className={`sm-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      <div className="sm-content">
        <div key={activeTab} className="sm-tab-panel">
          {activeTab === 'dashboard'   && <SentimentDashboard />}
          {activeTab === 'alerts'      && <SentimentAlerts onOpenWorkflow={openWorkflow} showToast={showToast} />}
          {activeTab === 'workflow'    && <SentimentWorkflow alertId={selectedAlertId} onBack={() => setActiveTab('alerts')} showToast={showToast} />}
          {activeTab === 'analytics'   && <SentimentAnalytics />}
          {activeTab === 'automations' && <SentimentAutomations showToast={showToast} />}
          {activeTab === 'copilot'     && <SentimentCopilot showToast={showToast} />}
          {activeTab === 'reports'     && <SentimentReports showToast={showToast} />}
          {activeTab === 'audit'       && <SentimentAudit />}
          {activeTab === 'monitoring'  && ['admin','team_lead'].includes(currentRole) && <SentimentMonitoring showToast={showToast} />}
          {activeTab === 'users'       && currentRole === 'admin' && <SentimentUsers showToast={showToast} />}
          {activeTab === 'rbac'        && currentRole === 'admin' && <SentimentRBAC showToast={showToast} />}
        </div>
      </div>
    </div>
  );
};

export default SentimentApp;
